'use client';

import { useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import Icon from '@/components/ui/AppIcon';
import { useToast } from '@/contexts/ToastContext';

interface InvoiceDownloadButtonProps {
  orderNumber: string;
  purchaseId: string;
  buyerName: string;
  buyerEmail: string;
  templateName: string;
  licenseQty?: number;
  giftRecipient?: string | null;
  totalPaid: number;
  purchaseDate: string;
  variant?: 'button' | 'link';
}

export default function InvoiceDownloadButton({
  orderNumber,
  purchaseId,
  buyerName,
  buyerEmail,
  templateName,
  licenseQty = 1,
  giftRecipient,
  totalPaid,
  purchaseDate,
  variant = 'button',
}: InvoiceDownloadButtonProps) {
  const [loading, setLoading] = useState(false);
  const { showSuccess, showError } = useToast();

  const handleDownload = async () => {
    try {
      setLoading(true);
      const invoiceNumber = `INV-${orderNumber}-${Date.now().toString(36).toUpperCase()}`;
      const unitPrice = totalPaid / licenseQty;
      const subtotal = totalPaid;

      const invoiceHTML = buildInvoiceHTML({
        invoiceNumber,
        orderNumber,
        date: new Date(purchaseDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        buyerName,
        buyerEmail,
        templateName,
        licenseQty,
        giftRecipient: giftRecipient || null,
        subtotal,
        tax: 0,
        total: subtotal,
        currency: 'USD',
        unitPrice,
      });

      const blob = new Blob([invoiceHTML], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${orderNumber}.html`;
      a.click();
      URL.revokeObjectURL(url);

      // Save invoice record
      try {
        await supabase.from('order_invoices').upsert({
          order_number: orderNumber,
          purchase_id: purchaseId,
          buyer_id: purchaseId,
          invoice_number: invoiceNumber,
          billing_name: buyerName,
          billing_email: buyerEmail,
          template_name: templateName,
          license_quantity: licenseQty,
          gift_recipients: giftRecipient ? [giftRecipient] : [],
          subtotal,
          tax_amount: 0,
          total_paid: totalPaid,
        }, { onConflict: 'order_number' });
      } catch {
        // Non-critical: invoice still downloaded
      }

      showSuccess('Invoice downloaded!');
    } catch (error: any) {
      showError(error.message || 'Failed to generate invoice');
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'link') {
    return (
      <button
        onClick={handleDownload}
        disabled={loading}
        className="text-xs flex items-center gap-1 hover:underline"
        style={{ color: 'var(--accent)' }}
      >
        <Icon name="DocumentArrowDownIcon" size={13} />
        {loading ? 'Generating...' : 'Download Invoice'}
      </button>
    );
  }

  return (
    <button
      onClick={handleDownload}
      disabled={loading}
      className="btn-secondary text-sm py-2 px-3 flex items-center gap-2"
    >
      <Icon name="DocumentArrowDownIcon" size={15} />
      {loading ? 'Generating...' : 'Invoice'}
    </button>
  );
}

function buildInvoiceHTML(data: {
  invoiceNumber: string;
  orderNumber: string;
  date: string;
  buyerName: string;
  buyerEmail: string;
  templateName: string;
  licenseQty: number;
  giftRecipient: string | null;
  subtotal: number;
  tax: number;
  total: number;
  currency: string;
  unitPrice: number;
}) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>Invoice ${data.invoiceNumber}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 40px; color: #111; background: #fff; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; }
  .brand { font-size: 24px; font-weight: 700; color: #6366f1; }
  .invoice-meta { text-align: right; }
  .invoice-meta h1 { font-size: 32px; font-weight: 800; color: #111; margin: 0 0 4px; }
  .invoice-meta p { margin: 2px 0; color: #666; font-size: 14px; }
  .section { margin-bottom: 32px; }
  .section h3 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.1em; color: #999; margin-bottom: 8px; }
  .bill-to p { margin: 2px 0; font-size: 15px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
  th { text-align: left; padding: 10px 12px; background: #f5f5f5; font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; color: #666; }
  td { padding: 12px; border-bottom: 1px solid #eee; font-size: 14px; }
  .totals { margin-left: auto; width: 280px; }
  .totals tr td { border: none; padding: 6px 12px; }
  .totals tr.total td { font-weight: 700; font-size: 16px; border-top: 2px solid #111; padding-top: 12px; }
  .footer { margin-top: 48px; padding-top: 24px; border-top: 1px solid #eee; font-size: 12px; color: #999; text-align: center; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 12px; background: #ede9fe; color: #7c3aed; }
</style>
</head>
<body>
<div class="header">
  <div class="brand">ZyForge</div>
  <div class="invoice-meta">
    <h1>INVOICE</h1>
    <p>${data.invoiceNumber}</p>
    <p>${data.date}</p>
  </div>
</div>
<div class="section bill-to">
  <h3>Bill To</h3>
  <p><strong>${data.buyerName}</strong></p>
  <p>${data.buyerEmail}</p>
</div>
<div class="section">
  <h3>Order Details</h3>
  <p style="font-size:14px;color:#666;">Order #${data.orderNumber}</p>
</div>
<table>
  <thead>
    <tr>
      <th>Description</th>
      <th>Qty</th>
      <th>Unit Price</th>
      <th>Total</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>
        ${data.templateName} — License Key
        ${data.giftRecipient ? `<br/><span class="badge">&#127873; Gift to ${data.giftRecipient}</span>` : ''}
      </td>
      <td>${data.licenseQty}</td>
      <td>$${data.unitPrice.toFixed(2)}</td>
      <td>$${data.subtotal.toFixed(2)}</td>
    </tr>
  </tbody>
</table>
<table class="totals">
  <tbody>
    <tr><td>Subtotal</td><td style="text-align:right">$${data.subtotal.toFixed(2)}</td></tr>
    ${data.tax > 0 ? `<tr><td>Tax</td><td style="text-align:right">$${data.tax.toFixed(2)}</td></tr>` : ''}
    <tr class="total"><td>Total Paid (${data.currency})</td><td style="text-align:right">$${data.total.toFixed(2)}</td></tr>
  </tbody>
</table>
<div class="footer">
  <p>ZyForge — Templates licensed for your use, not sold. All rights reserved.</p>
  <p>This invoice was generated automatically. For support, contact support@zyforge.com</p>
</div>
</body>
</html>`;
}
